import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles.ts';
import { ModalWrapper } from '@shared/ui/modal-wrapper';
import EmployeeSwitcher from '@entities/employee-switcher/index.tsx';
import CompaniesStructure from './companies-structure.tsx';
import { FreelanceEmployee } from './freelance-employee.tsx';
import WithExcel from './with-excel.tsx';
import { IFreelanceEmployee } from '@shared/types/types.ts';
import { useCreateTickets } from '@shared/api/events';
import { cleanPhoneNumber } from '@shared/libs/clean-number.ts';
import { ErrorData, useCreateUser } from '@features/api/use-create-user';
import { useFetchDeleteTicket } from '@features/api/use-delete-tickets.ts';
import { useToast } from '@shared/hooks/useToast.ts';
import { InvitedParticipants } from '@widgets/segments-tab';
import { PreloaderCircular } from '@shared/ui/kit';
import { Ticket } from '@pages/event/api/useFetchTicketSegment';
import { CreateFreelanceResultModal } from '@entities/freelance-employee';
import { useUpdateUser } from '@features/api/use-update-user.ts';
import { FreelanceEmployeeStep, PARTICIPANT_TAB_INDEX } from './model/types';
export interface ISegmentParticipants {
  id: number;
  title: string;
  eventId?: number;
  countTicket: number;
  freelanceEmployee?: IFreelanceEmployee[];
  companiesStructureList?: {
    [key: number]: number;
  };
  participants?: Ticket[];
  companiesStructureTree?: {
    tags?: Array<{
      id?: number;
      title?: string;
    }>;
  };
}
interface GuestExcel {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}
interface AddParticipantsModalProps {
  isOpenModal: boolean;
  setIsOpenModal: (isOpenModal: boolean) => void;
  segment: { id: number; title: string; eventId: number; countTicket: number };
  invitedParticipants: InvitedParticipants[];
  onSaveParticipants?: (
    participantIds: number[],
    options?: { shouldRefetchSegments?: boolean }
  ) => void | Promise<unknown>;
  onTicketsDeleted?: (ids: number[]) => void;
}
export const AddParticipantsModal: React.FC<AddParticipantsModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  segment,
  invitedParticipants,
  onSaveParticipants,
  onTicketsDeleted,
}) => {
  const [freelanceStep, setFreelanceStep] = useState<FreelanceEmployeeStep>(FreelanceEmployeeStep.STRUCTURE);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectedParticipants, setSelectedParticipants] = useState<number[]>([]);
  const [freelanceEmployees, setFreelanceEmployees] = useState<IFreelanceEmployee[]>([]);
  const [participantsDelete, setParticipantsDelete] = useState<InvitedParticipants[]>([]);
  const [excelImportedUserIds, setExcelImportedUserIds] = useState<number[]>([]);
  const [excelImportedGuests, setExcelImportedGuests] = useState<IFreelanceEmployee[]>([]);
  const [statusCreateEmployee, setStatusCreateEmployee] = useState('');
  const [errorsCreateEmployee, setErrorsCreateEmployee] = useState<ErrorData[]>([]);
  const [editableEmployee, setEditableEmployee] = useState<IFreelanceEmployee>();
  const [guestSelectedParticipants, setGuestSelectedParticipants] = useState<number[]>([]);

  useEffect(() => {}, [excelImportedGuests]);
  const [guestParticipantsDelete, setGuestParticipantsDelete] = useState<InvitedParticipants[]>([]);
  const { showToast } = useToast();
  const { createTickets, fetching: createTicketsFetching } = useCreateTickets();
  const { deleteTickets, fetching: deleteTicketsFetching } = useFetchDeleteTicket();
  const { createUser } = useCreateUser();
  const { updateUser } = useUpdateUser();
  const isLoading = createTicketsFetching || deleteTicketsFetching;
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [hasInvalidEmail, setHasInvalidEmail] = useState(false);
  useEffect(() => {
    if (freelanceEmployees && freelanceEmployees.length > 0) {
      const isValid = freelanceEmployees.every((employee) => {
        return (
          employee.firstName &&
          employee.lastName &&
          employee.firstName.trim() !== '' &&
          employee.lastName.trim() !== '' &&
          employee.phone &&
          employee.email &&
          cleanPhoneNumber(employee.phone).length >= 10
        );
      });
      setIsSaveButtonDisabled(!isValid || hasInvalidEmail);
    } else {
      setIsSaveButtonDisabled(false);
    }
  }, [freelanceEmployees, hasInvalidEmail]);
  const onEmailError = useCallback((hasError: boolean) => {
    setHasInvalidEmail(hasError);
  }, []);
  const onCloseFeedbackModal = () => {
    setStatusCreateEmployee('');
    setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
    setFreelanceEmployees([]);
    setEditableEmployee(undefined);
    setErrorsCreateEmployee([]);
  };
  const handelChangeSegment = async () => {
    if (activeIndex === PARTICIPANT_TAB_INDEX.FREELANCE_EMPLOYEE && freelanceStep === FreelanceEmployeeStep.FORM) {
      try {
        if (freelanceEmployees) {
          const hasEmptyFields = freelanceEmployees.some(
            (employee) => !employee.firstName || !employee.lastName || !employee.phone || !employee.email
          );
          if (hasEmptyFields) {
            showToast('Поля для внештатного участника не могут быть пустыми', 'error');
            setIsSaveButtonDisabled(true);
            return;
          }
          const newFreelanceEmployees = freelanceEmployees.map((employee) => ({
            firstName: employee.firstName,
            lastName: employee.lastName,
            phone: cleanPhoneNumber(employee.phone),
            email: employee.email,
          }));

          if (editableEmployee) {
            const { data, error } = await updateUser({
              participantId: editableEmployee.id,
              input: newFreelanceEmployees[0],
            });

            if (data?.updateParticipant?.errorData?.errorText) {
              setErrorsCreateEmployee([data?.updateParticipant?.errorData]);
              return;
            }

            if (error) {
              setStatusCreateEmployee('error');
              return;
            }

            setStatusCreateEmployee('success');
            setErrorsCreateEmployee([]);
            return;
          }

          const { data, error } = await createUser({ input: newFreelanceEmployees });

          if (data?.createParticipant.errorsData?.length || error) {
            setErrorsCreateEmployee(data?.createParticipant?.errorsData || []);
            return;
          }

          if (data?.createParticipant?.successIds) {
            const newIds = data?.createParticipant?.successIds.map(Number);
            setGuestSelectedParticipants((prev) => [...prev, ...newIds]);
          }

          setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
          setFreelanceEmployees([]);
          setErrorsCreateEmployee([]);
        }
      } catch {
        setStatusCreateEmployee('error');
      }
    } else {
      const ids = [...selectedParticipants, ...guestSelectedParticipants, ...excelImportedUserIds];
      const guests = [...excelImportedGuests];
      const manualGuests = [...freelanceEmployees];
      const allGuests = [...manualGuests, ...guests];
      try {
        const allParticipantsToDelete = [...participantsDelete, ...guestParticipantsDelete];
        if (allParticipantsToDelete.length > 0) {
          const idsToDelete = allParticipantsToDelete.map((ticket) => ticket.ticketId);
          const response = await deleteTickets({ ticketIds: idsToDelete });
          if (response.error) {
            throw new Error('Ошибка при удалении участников');
          }
          onTicketsDeleted?.(idsToDelete);
        }
        setSelectedParticipants([]);
        await createTickets({
          participantIds: ids,
          eventId: segment.eventId,
          segmentId: segment.id,
          guestsData: allGuests.map((e) => ({
            firstName: e.firstName || '',
            lastName: e.lastName || '',
            email: e.email || '',
            phone: e.phone || '',
          })),
        });
        await onSaveParticipants?.(ids, { shouldRefetchSegments: !segment.countTicket });
        setExcelImportedGuests([]);
        setExcelImportedUserIds([]);
        setGuestSelectedParticipants([]);
        setIsOpenModal(false);
      } catch (error) {
        showToast('Ошибка при изменении сегмента', 'error');
      }
    }
  };
  const handleCancel = () => {
    if (activeIndex === PARTICIPANT_TAB_INDEX.FREELANCE_EMPLOYEE && freelanceStep === FreelanceEmployeeStep.FORM) {
      setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
    } else {
      setSelectedParticipants([]);
      setParticipantsDelete([]);
      setGuestSelectedParticipants([]);
      setGuestParticipantsDelete([]);
      setActiveIndex(0);
      setIsOpenModal(false);
    }
    setEditableEmployee(undefined);
    setFreelanceEmployees([]);
    setHasInvalidEmail(false);
    setErrorsCreateEmployee([]);
  };
  const onChangeTab = (index: number) => {
    setActiveIndex(index);
    setEditableEmployee(undefined);
    setFreelanceEmployees([]);
    setFreelanceStep(FreelanceEmployeeStep.STRUCTURE);
    setErrorsCreateEmployee([]);
  };
  const formsForAdd: ReactNode[] = [
    <CompaniesStructure
      key="regular"
      source="1cuu"
      showFilter={true}
      invitedParticipants={invitedParticipants}
      selectedParticipants={selectedParticipants}
      setSelectedParticipants={setSelectedParticipants}
      setParticipantsDelete={setParticipantsDelete}
      participantsDelete={participantsDelete}
    />,
    <WithExcel
      key="excel"
      onSuccessUpload={(guestDataOrIds) => {
        if (Array.isArray(guestDataOrIds) && guestDataOrIds.length > 0) {
          const maybeGuest = guestDataOrIds[0];
          if (
            typeof maybeGuest === 'object' &&
            maybeGuest !== null &&
            'first_name' in maybeGuest &&
            'last_name' in maybeGuest &&
            'email' in maybeGuest &&
            'phone' in maybeGuest
          ) {
            const guestRows = guestDataOrIds as unknown as GuestExcel[];
            setExcelImportedGuests(
              guestRows.map((g) => ({
                firstName: g.first_name,
                lastName: g.last_name,
                email: g.email,
                phone: g.phone,
              }))
            );
            setExcelImportedUserIds([]);
            return;
          }
          if (typeof maybeGuest === 'number') {
            setExcelImportedUserIds(guestDataOrIds as number[]);
            setExcelImportedGuests([]);
            return;
          }
        }
        setExcelImportedUserIds([]);
        setExcelImportedGuests([]);
      }}
      setShowModal={() => {}}
    />,
    <FreelanceEmployee
      key="freelance"
      freelanceEmployees={freelanceEmployees}
      onChange={setFreelanceEmployees}
      onEmailError={onEmailError}
      guestSelectedParticipants={guestSelectedParticipants}
      setGuestSelectedParticipants={setGuestSelectedParticipants}
      setGuestParticipantsDelete={setGuestParticipantsDelete}
      guestParticipantsDelete={guestParticipantsDelete}
    />,
  ];
  return (
    <ModalWrapper
      active={isOpenModal}
      title={
        editableEmployee ? 'Редактирование внештатного участника' : `Добавить участников в сегмент: ${segment.title}`
      }
      size="small"
      settingsContainer={{ height: '554px' }}
      settingsHeader={{ width: '920px' }}
      {...stylex.props(styles.base)}
      buttons={
        !isLoading
          ? [
              {
                children: <p {...stylex.props(styles.textBtn, styles.cancelBtn)}>Отмена</p>,
                variant: 'secondary',
                onClick: handleCancel,
                settings: { width: '97px' },
              },
              {
                children: (
                  <p {...stylex.props(styles.textBtn, styles.acceptBtn)}>
                    {editableEmployee ? 'Сохранить' : 'Добавить'}
                  </p>
                ),
                variant: 'accent',
                onClick: handelChangeSegment,
                isDisabled: isSaveButtonDisabled,
                settings: { width: '97px' },
              },
            ]
          : undefined
      }
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <PreloaderCircular size="large" />
        </div>
      ) : (
        <div {...stylex.props(styles.modalContent)}>
          <EmployeeSwitcher
            activeIndex={activeIndex}
            setActiveIndex={onChangeTab}
            blocks={['Орг. структура', 'Импортировать из Excel', 'Внештатный участник']}
          />
          {activeIndex === PARTICIPANT_TAB_INDEX.FREELANCE_EMPLOYEE ? (
            <FreelanceEmployee
              freelanceEmployees={freelanceEmployees}
              onChange={setFreelanceEmployees}
              onEmailError={onEmailError}
              guestSelectedParticipants={guestSelectedParticipants}
              setGuestSelectedParticipants={setGuestSelectedParticipants}
              setGuestParticipantsDelete={setGuestParticipantsDelete}
              guestParticipantsDelete={guestParticipantsDelete}
              step={freelanceStep}
              invitedParticipants={invitedParticipants}
              setStep={setFreelanceStep}
              errors={errorsCreateEmployee}
              editableEmployee={editableEmployee}
              setEditableEmployee={setEditableEmployee}
            />
          ) : (
            formsForAdd[activeIndex]
          )}
        </div>
      )}
      <CreateFreelanceResultModal status={statusCreateEmployee} onClose={() => onCloseFeedbackModal()} />
    </ModalWrapper>
  );
};
